import { Outlet, Link, useLocation } from "react-router-dom";
import { 
  Brain,
  Home, 
  Users, 
  Menu,
  X,
  Trash2,
  FileText,
  Shield,
  Lock,
  Mail,
  UserPlus,
  BarChart3,
  Send,
  CreditCard,
  Building2,
  Printer,
  IdCard,
  PenLine,
  MessageSquare,
  LayoutList,
  Key,
  Activity,
  Bell,
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import ActivityLast24Sidebar from "./ActivityLast24Sidebar";

const navItems = [
  { path: "/", label: "Dashboard", icon: Home },
  { path: "/activity", label: "Last 24 Hours", icon: Activity },
  { path: "/clients", label: "Clients", icon: Users },
  { path: "/client-updates", label: "Client Updates", icon: MessageSquare },
  { path: "/trash", label: "Trash", icon: Trash2 },
];

const toolItems = [
  { path: "/payday-st", label: "Payday ST", icon: FileText, color: "text-amber-400" },
  { path: "/copy-fax", label: "Copy & Fax Converter", icon: Printer, color: "text-blue-400" },
  { path: "/id-converter", label: "ID + SSN Converter", icon: IdCard, color: "text-cyan-400" },
  { path: "/handwriting-converter", label: "Handwritten Converter", icon: PenLine, color: "text-violet-400" },
  { path: "/letter-intelligence", label: "Letter Intelligence", icon: Brain, color: "text-violet-300" },
  { path: "/pdf-finalize", label: "PDF Finalize", icon: Lock, color: "text-purple-400" },
  { path: "/account-summary-breakdown", label: "Account Summary Breakdown", icon: LayoutList, color: "text-fuchsia-400" },
  { path: "/just-mail", label: "Mail", icon: Mail, color: "text-purple-400" },
  { path: "/send-intake", label: "Send Intake", icon: UserPlus, color: "text-teal-400" },
  { path: "/analyze-report", label: "Analyze Report", icon: BarChart3, color: "text-blue-400" },
  { path: "/credit-repair-steps", label: "Credit Repair Steps", icon: Send, color: "text-green-400" },
  { path: "/tradeline-manager", label: "Tradeline Manager", icon: CreditCard, color: "text-pink-400" },
  { path: "/tradeline-world", label: "Tradeline World", icon: CreditCard, color: "text-fuchsia-400" },
  { path: "/credit-builder-list", label: "Credit Builder List", icon: Building2, color: "text-cyan-400" },
  { path: "/apartment-lease", label: "Apartment Lease Creator", icon: Key, color: "text-orange-400" },
  { path: "/email-reminders", label: "Email Reminders", icon: Bell, color: "text-emerald-400" },
];

export default function Layout() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="bg-zinc-900 hover:bg-zinc-800"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-zinc-900 border-r border-zinc-800 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-zinc-800">
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/clear-all-wizard-logo.png" alt="Clear All Wizard" className="h-12 w-auto" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Main Nav */}
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? "bg-orange-500/20 text-orange-400 border border-orange-500/30"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Tools Section */}
            <div className="pt-4 mt-4 border-t border-zinc-700">
              <p className="px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Tools</p>
              {toolItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                      isActive
                        ? "bg-zinc-800 text-white border border-zinc-600"
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${item.color}`} />
                    <span className="text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Past 24 Hours — sidebar bottom (2026-02-08 user request) */}
            <ActivityLast24Sidebar />
          </nav>

          {/* Quick Actions - Removed New Dispute and CT Convert */}
          <div className="p-4 border-t border-zinc-800 space-y-1">
            <p className="text-xs text-zinc-500 text-center">v2.0 - Clear All Wizard</p>
            <BuildBanner />
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:pl-64 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/**
 * BuildBanner — fetches the backend's build date once on mount and
 * renders a small pill in the sidebar footer.  When production is
 * running an older deploy this number will lag behind preview so the
 * user can spot at a glance whether a redeploy is needed.
 */
function BuildBanner() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios
      .get(`${base}/api/version`, { timeout: 8000 })
      .then((res) => setInfo(res.data))
      .catch(() => setInfo({ build_date: "offline" }));
  }, []);
  if (!info) return null;
  return (
    <div
      className="text-[10px] text-center text-zinc-600 font-mono tracking-tight"
      title={info.iso || ""}
      data-testid="sidebar-build-banner"
    >
      Build {info.build_date}
    </div>
  );
}
